/**
 * Comparação de um par de duplicatas, lado a lado, antes de fundir.
 *
 * Fundir apaga um dos cadastros — decidir isso vendo só dois nomes parecidos é apostar. Aqui o
 * operador vê os dois cadastros inteiros: os campos de identidade (com o que **coincide**, o que
 * **diverge** e o que só um lado tem) e tudo que cada um carrega — contatos, relações, empresas,
 * condomínios, imóveis, veículos, processos e transações. Campo divergente é destacado em vermelho:
 * dois CPFs ou duas mães diferentes são a prova de que NÃO é a mesma pessoa.
 *
 * A fusão sai daqui na direção que o operador escolher (qual dos dois mantém).
 */
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeftRight, GitMerge, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';

type Campo = {
  campo: string;
  a: string | null;
  b: string | null;
  situacao: 'igual' | 'divergente' | 'so_um';
};

type LadoDossie = {
  id?: string;
  nome?: string;
  telefones?: string[];
  emails?: string[];
  enderecos?: string[];
  relacoes?: { nome?: string; tipo?: string; rotulo?: string }[];
  empresas?: { razao_social?: string; nome?: string; cargo?: string }[];
  condominios?: { nome?: string; papel?: string }[];
  imoveis?: { identificacao?: string; papel?: string }[];
  veiculos?: { placa?: string; modelo?: string; marca?: string }[];
  processos?: { numero?: string; polo?: string }[];
  transacoes?: { natureza?: string; valor?: string | number; data?: string }[];
};

type Comparacao = {
  id: string;
  score: number;
  sinais: { sinais?: string[] };
  campos: Campo[];
  divergencias: number;
  a: LadoDossie;
  b: LadoDossie;
};

const ROTULOS: Record<string, string> = {
  nome: 'Nome',
  cpf: 'CPF',
  rg: 'RG',
  data_nascimento: 'Nascimento',
  idade: 'Idade',
  nome_mae: 'Mãe',
  nome_pai: 'Pai',
  sexo: 'Sexo',
  estado_civil: 'Estado civil',
  naturalidade_cidade: 'Naturalidade',
  naturalidade_uf: 'UF de nascimento',
  cnh: 'CNH',
  titulo_eleitor: 'Título de eleitor',
  pis_nis: 'PIS/NIS',
  profissao_id: 'Profissão',
  observacoes: 'Observações',
};

/** Cada bloco do dossiê vira uma lista de linhas em texto — o modal compara, não edita. */
function linhasDoLado(d: LadoDossie): { titulo: string; itens: string[] }[] {
  const blocos: { titulo: string; itens: string[] }[] = [
    { titulo: 'Telefones', itens: d.telefones ?? [] },
    { titulo: 'E-mails', itens: d.emails ?? [] },
    { titulo: 'Endereços', itens: d.enderecos ?? [] },
    {
      titulo: 'Relações',
      itens: (d.relacoes ?? []).map((r) =>
        [r.tipo, r.nome ?? r.rotulo].filter(Boolean).join(': '),
      ),
    },
    {
      titulo: 'Empresas',
      itens: (d.empresas ?? []).map((e) =>
        [e.razao_social ?? e.nome, e.cargo].filter(Boolean).join(' — '),
      ),
    },
    {
      titulo: 'Condomínios',
      itens: (d.condominios ?? []).map((c) => [c.nome, c.papel].filter(Boolean).join(' — ')),
    },
    {
      titulo: 'Imóveis',
      itens: (d.imoveis ?? []).map((i) => [i.identificacao, i.papel].filter(Boolean).join(' — ')),
    },
    {
      titulo: 'Veículos',
      itens: (d.veiculos ?? []).map((v) =>
        [v.placa, v.marca, v.modelo].filter(Boolean).join(' '),
      ),
    },
    { titulo: 'Processos', itens: (d.processos ?? []).map((p) => p.numero ?? '—') },
    {
      titulo: 'Transações',
      itens: (d.transacoes ?? []).map((t) =>
        [t.natureza, t.data, t.valor].filter(Boolean).join(' — '),
      ),
    },
  ];
  return blocos.filter((b) => b.itens.length > 0);
}

function Lado({ dossie, papel }: { dossie: LadoDossie; papel: 'mantido' | 'absorvido' }) {
  const blocos = linhasDoLado(dossie);
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div
        className={
          'rounded-md border px-3 py-2 ' +
          (papel === 'mantido'
            ? 'border-accent bg-surface-2'
            : 'border-border bg-surface-2 opacity-80')
        }
      >
        <span className="tipo-rotulo block text-text-tertiary">
          {papel === 'mantido' ? 'Mantido (sobrevive)' : 'Absorvido (apagado)'}
        </span>
        <span className={'tipo-dado ' + (papel === 'mantido' ? 'text-accent' : 'text-text')}>
          {dossie.nome ?? '—'}
        </span>
      </div>
      {blocos.length === 0 ? (
        <p className="tipo-corpo-pequeno text-text-tertiary">Sem vínculos registrados.</p>
      ) : (
        blocos.map((b) => (
          <div key={b.titulo} className="flex flex-col gap-1">
            <span className="tipo-rotulo text-text-tertiary">
              {b.titulo} ({b.itens.length})
            </span>
            <ul className="flex flex-col gap-0.5">
              {b.itens.map((linha, i) => (
                <li key={`${b.titulo}-${i}`} className="tipo-corpo-pequeno text-text-secondary">
                  {linha || '—'}
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}

export function ModalCompararPar({
  candidataId,
  invertido,
  onInverter,
  onFechar,
  onFundir,
  ocupado = false,
}: {
  /** Par a comparar; `null` mantém o modal fechado. */
  candidataId: string | null;
  /** Quando verdadeiro, quem sobrevive é o lado B. */
  invertido: boolean;
  onInverter: () => void;
  onFechar: () => void;
  onFundir: () => void;
  ocupado?: boolean;
}) {
  const [dados, setDados] = useState<Comparacao | null>(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (candidataId === null) return void setDados(null);
    let vivo = true;
    setCarregando(true);
    void api
      .get<Comparacao>(`/cadastro/dedup/candidatas/${candidataId}/comparar`)
      .then((r) => {
        if (!vivo) return;
        setDados(r.ok ? r.dados : null);
        setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, [candidataId]);

  const mantido = dados ? (invertido ? dados.b : dados.a) : {};
  const absorvido = dados ? (invertido ? dados.a : dados.b) : {};

  return (
    <Dialog.Root open={candidataId !== null} onOpenChange={(v) => !v && onFechar()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[var(--z-backdrop)] bg-[rgba(13,16,23,.6)] backdrop-blur-sm" />
        <Dialog.Content
          className={
            'fixed z-[var(--z-modal)] flex flex-col overflow-hidden bg-surface ' +
            'inset-0 md:inset-auto md:left-1/2 md:top-1/2 md:h-[85vh] md:w-[min(56rem,90vw)] ' +
            'md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg md:border md:border-border md:shadow-[var(--shadow-lg)]'
          }
        >
          <header className="flex items-center justify-between gap-3 border-b border-border p-4">
            <div className="min-w-0">
              <Dialog.Title className="tipo-subtitulo truncate text-text">
                Comparar antes de fundir
              </Dialog.Title>
              <Dialog.Description className="tipo-corpo-pequeno text-text-tertiary">
                {dados
                  ? `score ${dados.score.toFixed(2)} · ${(dados.sinais.sinais ?? []).join(', ')}`
                  : 'Carregando os dois cadastros…'}
              </Dialog.Description>
            </div>
            <Dialog.Close
              aria-label="Fechar"
              className="rounded-md p-2 text-text-secondary outline-none transition-colors hover:bg-surface-hover hover:text-text focus-visible:shadow-[var(--focus-ring)]"
            >
              <X className="size-4" />
            </Dialog.Close>
          </header>

          <div className="flex-1 overflow-y-auto p-4">
            {carregando || dados === null ? (
              <div className="grid min-h-40 place-items-center">
                {carregando ? <Spinner /> : <span className="tipo-corpo">Par indisponível.</span>}
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {dados.divergencias > 0 && (
                  <p className="rounded-md border border-danger px-3 py-2 tipo-corpo-pequeno text-text-secondary">
                    <b className="text-danger">
                      {dados.divergencias} campo(s) com valores diferentes nos dois cadastros.
                    </b>{' '}
                    Confira antes: valores conflitantes de identidade (CPF, mãe, nascimento) são o
                    sinal mais forte de que são pessoas <b>diferentes</b>.
                  </p>
                )}

                <div className="flex flex-col gap-1">
                  <div className="grid grid-cols-[10rem_1fr_1fr] gap-2 border-b border-border pb-1">
                    <span className="tipo-rotulo text-text-tertiary">Campo</span>
                    <span className="tipo-rotulo text-accent">
                      {invertido ? 'Absorvido' : 'Mantido'}
                    </span>
                    <span className="tipo-rotulo text-text-tertiary">
                      {invertido ? 'Mantido' : 'Absorvido'}
                    </span>
                  </div>
                  {dados.campos.map((c) => (
                    <div
                      key={c.campo}
                      className="grid grid-cols-[10rem_1fr_1fr] gap-2 border-b border-border/50 py-1"
                    >
                      <span className="tipo-corpo-pequeno text-text-tertiary">
                        {ROTULOS[c.campo] ?? c.campo}
                      </span>
                      <span
                        className={
                          'tipo-corpo-pequeno break-words ' +
                          (c.situacao === 'divergente' ? 'text-danger' : 'text-text-secondary')
                        }
                      >
                        {c.a ?? '—'}
                      </span>
                      <span
                        className={
                          'tipo-corpo-pequeno break-words ' +
                          (c.situacao === 'divergente' ? 'text-danger' : 'text-text-secondary')
                        }
                      >
                        {c.b ?? '—'}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Lado dossie={mantido} papel="mantido" />
                  <Lado dossie={absorvido} papel="absorvido" />
                </div>
              </div>
            )}
          </div>

          <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-border p-4">
            <Button variant="fantasma" onClick={onInverter} disabled={dados === null}>
              <ArrowLeftRight />
              Inverter quem sobrevive
            </Button>
            <div className="flex gap-2">
              <Button variant="secundario" onClick={onFechar}>
                Fechar
              </Button>
              <Button variant="primario" onClick={onFundir} disabled={ocupado || dados === null}>
                {ocupado ? <Spinner /> : <GitMerge />}
                Fundir este par
              </Button>
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
